import React, { useState } from "react";
import { ChevronDown, Plus, Trash2, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ICON_NAMES, Icon } from "@/lib/icons";

export function Collapsible({ title, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const slug = "cms-section-" + title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return (
    <div className="card-surface rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left" data-testid={slug}>
        <div>
          <p className="font-display font-semibold text-white">{title}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 pt-1 space-y-4 border-t border-white/10">{children}</div>}
    </div>
  );
}

// Plain (shared) field — for URLs, links, emails
export function Field({ label, value, onChange, multiline, placeholder, testid, rows = 3 }) {
  return (
    <div>
      <Label className="text-slate-300 text-sm">{label}</Label>
      {multiline
        ? <Textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className="mt-1.5 bg-white/5 border-white/10" />
        : <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className="mt-1.5 bg-white/5 border-white/10" />}
    </div>
  );
}

// Bilingual field — stores { en, ar } with EN | AR mini-tabs
export function LField({ label, value, onChange, multiline, placeholder, testid, rows = 3 }) {
  const [lng, setLng] = useState("en");
  const obj = value && typeof value === "object" ? value : { en: value || "", ar: "" };
  const set = (v) => onChange({ ...obj, [lng]: v });
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-slate-300 text-sm">{label}</Label>
        <div className="flex gap-1">
          {["en", "ar"].map((l) => (
            <button type="button" key={l} onClick={() => setLng(l)} data-testid={testid ? `${testid}-${l}` : undefined}
              className={`px-2 py-0.5 rounded text-[11px] font-mono border transition-colors ${lng === l ? "border-brand-cyan text-brand-cyan bg-brand-cyan/10" : "border-white/10 text-slate-500"}`}>
              {l.toUpperCase()}{(obj[l] || "").trim() ? " ✓" : ""}
            </button>
          ))}
        </div>
      </div>
      {multiline
        ? <Textarea dir={lng === "ar" ? "rtl" : "ltr"} rows={rows} value={obj[lng] || ""} onChange={(e) => set(e.target.value)} placeholder={placeholder} data-testid={testid} className="mt-1.5 bg-white/5 border-white/10" />
        : <Input dir={lng === "ar" ? "rtl" : "ltr"} value={obj[lng] || ""} onChange={(e) => set(e.target.value)} placeholder={placeholder} data-testid={testid} className="mt-1.5 bg-white/5 border-white/10" />}
      {lng === "ar" && !(obj.ar || "").trim() && <p className="text-[11px] text-amber-400 mt-1">Arabic translation missing — English will be shown as fallback.</p>}
    </div>
  );
}

export function IconPicker({ value, onChange }) {
  return (
    <div>
      <Label className="text-slate-300 text-sm">Icon</Label>
      <Select value={value || "Sparkles"} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-64">
          {ICON_NAMES.map((n) => (
            <SelectItem key={n} value={n}><span className="flex items-center gap-2"><Icon name={n} className="h-4 w-4 text-brand-cyan" /> {n}</span></SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Editable list of card items (services/benefits/values/steps). Field types: text, textarea, ltext, ltextarea, icon
export function ItemList({ items, setItems, fields, newItem, addLabel = "Add item" }) {
  const update = (i, key, val) => { const c = [...items]; c[i] = { ...c[i], [key]: val }; setItems(c); };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const add = () => setItems([...(items || []), { ...newItem, id: Math.random().toString(36).slice(2, 9), order: (items?.length || 0) }]);

  const renderField = (it, i, f) => {
    if (f.type === "icon") return <IconPicker key={f.key} value={it[f.key]} onChange={(v) => update(i, f.key, v)} />;
    if (f.type === "ltext" || f.type === "ltextarea")
      return <LField key={f.key} label={f.label} value={it[f.key]} onChange={(v) => update(i, f.key, v)} multiline={f.type === "ltextarea"} rows={f.rows} />;
    return <Field key={f.key} label={f.label} value={it[f.key]} onChange={(v) => update(i, f.key, v)} multiline={f.type === "textarea"} rows={f.rows} />;
  };

  return (
    <div className="space-y-3">
      {(items || []).map((it, i) => (
        <div key={it.id || i} className="rounded-lg border border-white/10 bg-brand-navy/40 p-4 space-y-3" data-testid={`cms-item-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500 flex items-center gap-1.5"><GripVertical className="h-3.5 w-3.5" /> Item {i + 1}</span>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-slate-400"><input type="checkbox" checked={it.active !== false} onChange={(e) => update(i, "active", e.target.checked)} /> Active</label>
              <button type="button" onClick={() => remove(i)} className="text-slate-500 hover:text-red-400" data-testid={`cms-item-del-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          {fields.map((f) => renderField(it, i, f))}
          <div className="w-28"><Field label="Order" value={it.order} onChange={(v) => update(i, "order", v)} /></div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={add} className="border-white/15 text-white w-full" data-testid="cms-item-add"><Plus className="h-4 w-4 mr-1.5" /> {addLabel}</Button>
    </div>
  );
}

export function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} /> {label}</label>
  );
}
